import RedoRoundedIcon from "@mui/icons-material/RedoRounded"
import UndoRoundedIcon from "@mui/icons-material/UndoRounded"
import FileUploadIcon from "@mui/icons-material/FileUpload"
import { Box, Tooltip, Button } from "@mui/material"
import { useState, useEffect } from "react"
import AppBar from "@mui/material/AppBar"
import CardMedia from "@mui/material/CardMedia"
import Grid from "@mui/material/Grid"
import IconButton from "@mui/material/IconButton"
import Toolbar from "@mui/material/Toolbar"
import { isMobileOnly, isWindows } from "react-device-detect"
import { useTranslation } from "react-i18next"
import { useLocation } from "react-router-dom"
import pathToLogo from "../assets/logo_tweenly_dark.svg"
import { getUserLicenseState, UserLicenseType } from "../helpers/license.helpers"
import { selectCanRedo, selectCanUndo } from "../helpers/selector.helpers"
import { RouteT } from "../hooks/router/useRoute"
import { useAppDispatch, useAppSelector } from "../hooks/useRedux"
import { isAdmin } from "../hooks/useSession"
import { getCompaniesStatsAsync } from "../store/slices/companiesStats.slice"
import { getPreferencesAsync, redo, undo } from "../store/slices/editor.slice"
import { getTimelinePreferencesAsync } from "../store/slices/timelinePreferences.slice"
import { EnumLicense } from "../store/slices/user.slice"
import MainMenu from "./MainMenu"
import TopNavbarEditorActions from "./TopNavbarEditorActions"
import TransitionAlerts from "./common/TransitionAlert"
import { PencilImportDialog } from "./Editor/PencilImport/PencilImportDialog"

interface Props {
    routes: RouteT[]
}

const TopNavbar = ({ routes }: Props) => {
    const { t } = useTranslation()
    const dispatch = useAppDispatch()
    const canUndo = useAppSelector((state) => selectCanUndo(state))
    const canRedo = useAppSelector((state) => selectCanRedo(state))
    const location = useLocation()
    const user = useAppSelector((state) => state.user.value)
    const roles = useAppSelector((state) => state.user.value.roles)
    const isSuperAdmin = isAdmin(roles)
    const [pencilImportOpen, setPencilImportOpen] = useState(false)

    const expiredUserLicense =
        user?.license?.expired && new Date(user.license.expired).getTime() < Date.now()
    const isExpired = isSuperAdmin ? false : expiredUserLicense

    const userLicenseState = getUserLicenseState(
        user.license?.license as EnumLicense,
        user.license?.started,
        user.license?.expired,
        user.createdAt,
        user.license?.isRevoked
    )
    const isFreeLicense = isSuperAdmin ? false : userLicenseState.type === UserLicenseType.Free

    const session = useAppSelector((state) => state.session)

    useEffect(() => {
        if (session.data) {
            dispatch(getPreferencesAsync())
            dispatch(getTimelinePreferencesAsync())
            dispatch(getCompaniesStatsAsync())
        }
    }, [dispatch, session.data])

    return (
        <>
            <AppBar position="relative" color="default" sx={{ position: "relative", zIndex: 1400 }}>
                <Toolbar>
                    <Grid container direction="row" alignItems={"center"}>
                        <MainMenu routes={routes} />

                        <CardMedia
                            image={pathToLogo}
                            sx={{ width: "100px", height: "25px", mx: 2 }}
                        />
                        {!isMobileOnly && location.pathname === "/app/editor" && (
                            <Grid item flexGrow={1}>
                                <Grid
                                    container
                                    direction="row"
                                    justifyContent="space-between"
                                    alignItems="center"
                                >
                                    <Grid item ml={2}>
                                        <Tooltip
                                            title={
                                                t("editor:undoButton") +
                                                (isWindows ? " | Ctrl Z" : " | ⌘ Z")
                                            }
                                        >
                                            <IconButton
                                                color="default"
                                                component="span"
                                                onClick={() => dispatch(undo())}
                                                disabled={!canUndo}
                                            >
                                                <UndoRoundedIcon />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip
                                            title={
                                                t("editor:redoButton") +
                                                (isWindows ? " | Ctrl Shift Z" : " | ⇧ ⌘ Z")
                                            }
                                        >
                                            <IconButton
                                                color="default"
                                                component="span"
                                                onClick={() => dispatch(redo())}
                                                disabled={!canRedo}
                                            >
                                                <RedoRoundedIcon />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title={t("editor:importDialog.buttonOpenTooltip")}>
                                            <Button
                                                startIcon={<FileUploadIcon />}
                                                onClick={() => setPencilImportOpen(true)}
                                                sx={{ ml: 2 }}
                                                size="small"
                                                variant="outlined"
                                            >
                                                {t("editor:importDialog.buttonOpen")}
                                            </Button>
                                        </Tooltip>
                                    </Grid>
                                    <Grid item>
                                        <TopNavbarEditorActions />
                                    </Grid>
                                </Grid>
                            </Grid>
                        )}
                    </Grid>
                </Toolbar>
            </AppBar>
            {(isExpired || isFreeLicense) && (
                <Box
                    sx={{
                        position: "fixed",
                        bottom: 0,
                        left: 0,
                        width: "100%",
                        zIndex: 1400,
                    }}
                >
                    <TransitionAlerts
                        severity="warning"
                        alertMessage={t(
                            `editor:license${isFreeLicense ? "Free" : "Expired"}Alert.regular`
                        )}
                        alertMessageBold={t(
                            `editor:license${isFreeLicense ? "Free" : "Expired"}Alert.bold`
                        )}
                    />
                </Box>
            )}
            <PencilImportDialog
                open={pencilImportOpen}
                onClose={() => setPencilImportOpen(false)}
            />
        </>
    )
}

export default TopNavbar
